import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Tab,
  Tabs,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { HOME_TAB_KEY } from 'store/workspaceTabs';

const WorkspaceTabs = ({activeKey, appBar, onClose, onSelect, tabs}) => {
  const tabRefs = useRef(new Map());
  const pendingFocusKey = useRef(null);
  const activeTab = tabs.find(tab => tab.key === activeKey);

  useEffect(() => {
    const focusKey = pendingFocusKey.current;
    const target = focusKey && tabRefs.current.get(focusKey);
    if (target) {
      target.focus();
      pendingFocusKey.current = null;
    }

    const keepActiveTabVisible = () => {
      const activeTarget = activeKey && tabRefs.current.get(activeKey);
      activeTarget?.scrollIntoView?.({block: 'nearest', inline: 'nearest'});
    };
    let visibilityTimer;
    const keepActiveTabVisibleAfterLayout = () => {
      clearTimeout(visibilityTimer);
      visibilityTimer = setTimeout(keepActiveTabVisible, 250);
    };
    keepActiveTabVisible();
    window.addEventListener('resize', keepActiveTabVisibleAfterLayout);
    return () => {
      clearTimeout(visibilityTimer);
      window.removeEventListener('resize', keepActiveTabVisibleAfterLayout);
    };
  }, [activeKey, tabs]);

  const closeTab = (event, key) => {
    event.preventDefault();
    event.stopPropagation();
    pendingFocusKey.current = onClose(key);
  };

  return (
    <Box
      sx={{
        alignItems: 'stretch',
        bgcolor: appBar ? 'transparent' : 'background.paper',
        borderBottom: appBar ? 0 : 1,
        borderColor: appBar ? 'transparent' : 'divider',
        color: appBar ? 'primary.contrastText' : 'text.primary',
        display: 'flex',
        flex: '1 1 auto',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Tabs
        allowScrollButtonsMobile
        aria-label="已打开页面"
        onChange={(_event, key) => onSelect(key)}
        scrollButtons="auto"
        sx={{
          flex: '1 1 auto',
          minHeight: appBar ? 48 : 40,
          minWidth: 0,
          ...(appBar && {
            color: 'primary.contrastText',
            '& .MuiTabs-indicator': {
              display: 'none',
            },
            '& .MuiTabs-scrollButtons': {color: 'inherit'},
            '& .MuiTab-root.Mui-selected': {
              alignSelf: 'flex-end',
              bgcolor: 'background.default',
              borderRadius: '6px 6px 0 0',
              minHeight: 42,
              color: 'text.primary',
              opacity: 1,
            },
            '& .MuiTab-root.Mui-selected .MuiTab-iconWrapper:hover': {
              bgcolor: 'action.hover',
            },
          }),
          '& .MuiTab-root': {
            minHeight: appBar ? 48 : 40,
            opacity: appBar ? 0.82 : 1,
            py: 0.75,
          },
        }}
        textColor={appBar ? 'inherit' : 'primary'}
        value={activeTab ? activeKey : false}
        variant="scrollable"
      >
        {tabs.map(tab => (
          <Tab
            aria-keyshortcuts={tab.key === HOME_TAB_KEY ? undefined : 'Delete'}
            icon={tab.key === HOME_TAB_KEY ? undefined : (
              <Box
                aria-hidden="true"
                component="span"
                onClick={event => closeTab(event, tab.key)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                sx={{
                  alignItems: 'center',
                  borderRadius: '50%',
                  display: 'inline-flex',
                  height: 24,
                  justifyContent: 'center',
                  opacity: 0.65,
                  width: 24,
                  '&:hover': {
                    bgcolor: appBar ? 'rgba(255, 255, 255, 0.16)' : 'action.hover',
                    opacity: 1,
                  },
                }}
                title={`关闭页签 ${tab.title}`}
              >
                <CloseIcon sx={{fontSize: 16}} />
              </Box>
            )}
            iconPosition="end"
            key={tab.key}
            label={tab.title}
            onKeyDown={(event) => {
              if (event.key === 'Delete' && tab.key !== HOME_TAB_KEY) {
                closeTab(event, tab.key);
              }
            }}
            ref={(node) => {
              if (node) {
                tabRefs.current.set(tab.key, node);
              } else {
                tabRefs.current.delete(tab.key);
              }
            }}
            value={tab.key}
          />
        ))}
      </Tabs>
    </Box>
  );
};

WorkspaceTabs.propTypes = {
  activeKey: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  appBar: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  })).isRequired,
};

export default WorkspaceTabs;
